import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/firebaseInit';
import { coll_bookingRequests, type BookingRequestType } from '@/types/types_bookingRequest';

// Data access for booking requests. Reads only.
//
// Neither creating a request nor moving one between states happens here:
// `createBookingRequest` makes one and `setBookingRequestStatus` moves it, both
// Cloud Functions (see src/util/callable_functions.ts). `bookingRequests` is
// closed to client writes in firestore.rules, so this file could not write to
// it even if it wanted to.

const bookingRequestsCollection = collection(db, coll_bookingRequests);

function toBookingRequest(id: string, data: Record<string, unknown>): BookingRequestType {
  return { ...(data as Omit<BookingRequestType, 'id'>), id };
}

/**
 * Subscribes to every booking request, newest first. Admins only — the security
 * rules refuse an unfiltered list to anyone else.
 *
 * Deliberately unfiltered by status: the queue is small, the CMS shows pending
 * and decided side by side, and one subscription keeps the dashboard's pending
 * count and the list itself from disagreeing.
 */
export function subscribeBookingRequests(
  onChange: (requests: BookingRequestType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(bookingRequestsCollection, orderBy('createdAt', 'desc')),
    (snap) => onChange(snap.docs.map((d) => toBookingRequest(d.id, d.data()))),
    onError,
  );
}

/**
 * Subscribes to one partner's own requests, newest first.
 *
 * The `partnerId` filter is not what makes this safe — the security rules are,
 * and they allow a read only to a member of that partner or to an admin. Asking
 * for somebody else's partner here does not return their requests, it returns a
 * permission error. The filter is what makes the query *possible* for a partner,
 * which is a different thing and worth not confusing.
 *
 * Live rather than fetched once, like the admin queue: a partner watching for an
 * answer should see a held date appear without reloading, and the offer email
 * may reach a colleague rather than whoever has this page open.
 */
export function subscribeBookingRequestsForPartner(
  partnerId: string,
  onChange: (requests: BookingRequestType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(bookingRequestsCollection, where('partnerId', '==', partnerId), orderBy('createdAt', 'desc')),
    (snap) => onChange(snap.docs.map((d) => toBookingRequest(d.id, d.data()))),
    onError,
  );
}

